/**
 * Regent UI — Component Loader
 *
 * A lightweight (~2KB) script that observes the DOM for <regent-*> custom elements
 * and dynamically fetches their definitions on demand.
 *
 * Usage:
 *   <script type="module" src="https://cdn.example.com/loader.js"></script>
 *   <!-- Components auto-load when used: -->
 *   <regent-button variant="primary">Click me</regent-button>
 */

// ── Configuration ──────────────────────────────────────────────────────────

/** Base URL where component JS files are served. */
const BASE_URL = (() => {
  // If the loader script itself has a data-base-url attribute, use that.
  const scriptEl = document.currentScript as HTMLScriptElement | null;
  if (scriptEl?.dataset.baseUrl) {
    return scriptEl.dataset.baseUrl.replace(/\/+$/, "");
  }
  // Otherwise derive from the loader's own src.
  if (scriptEl?.src) {
    try {
      const url = new URL(scriptEl.src);
      return url.origin + url.pathname.replace(/\/[^/]+$/, "");
    } catch {
      // ignore
    }
  }
  // Fallback: assume same origin, /components/ path.
  return "/components";
})();

/**
 * Registry mapping custom element tag names to their JS module filenames.
 * Add new components here as they are built.
 */
const COMPONENT_MAP: Record<string, string> = {
  "regent-button": "button.js",
  "regent-checkbox": "checkbox.js",
  "regent-person": "person.js",
  "regent-select": "select.js",
  // "regent-input": "input.js",
  // "regent-card": "card.js",
};

// ── State ──────────────────────────────────────────────────────────────────

/** Components whose module has already been fetched and defined. */
const loaded = new Set<string>();

/** Components currently being fetched (Promise in flight). */
const loading = new Map<string, Promise<void>>();

/** How many times a component module is fetched before the loader gives up. */
const MAX_LOAD_ATTEMPTS = 3;

/** Pause between attempts, so a transient network blip has time to clear. */
const RETRY_DELAY_MS = 250;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── Helpers ────────────────────────────────────────────────────────────────

function tagNameFromEl(el: Element): string {
  return el.tagName.toLowerCase();
}

function isRegentElement(tagName: string): boolean {
  return tagName.startsWith("regent-") && tagName in COMPONENT_MAP;
}

function componentURL(fileName: string): string {
  return `${BASE_URL}/${fileName}`;
}

/**
 * Build the module URL for a load attempt.
 *
 * The first attempt uses the plain URL so the browser's normal module map and
 * HTTP cache apply. Retries append a cache-busting query because a *failed*
 * dynamic import is cached by the browser's module map: re-importing the same
 * specifier rejects immediately without touching the network, so one transient
 * failure (a 404 while a deploy was rolling, an offline blip, a poisoned HTTP
 * cache entry) would otherwise leave the component permanently un-upgraded for
 * the life of the page — silently, with nothing in the network tab. A different
 * specifier is a different cache entry, so the retry genuinely re-fetches.
 */
function componentURLForAttempt(fileName: string, attempt: number): string {
  const url = componentURL(fileName);
  return attempt === 1 ? url : `${url}?regent-retry=${attempt}-${Date.now()}`;
}

/**
 * Check a single element and all its descendants for unloaded regent-* tags.
 */
function scanElement(el: Element): void {
  const tagName = tagNameFromEl(el);

  // Check the element itself
  if (isRegentElement(tagName)) {
    ensureLoaded(tagName);
  }

  // Check all descendants (querySelectorAll on custom elements works even before upgrade)
  const descendants = el.querySelectorAll<HTMLElement>(
    Object.keys(COMPONENT_MAP).join(","),
  );
  for (const descendant of descendants) {
    ensureLoaded(tagNameFromEl(descendant));
  }
}

/**
 * Ensure a component's module is loaded. Deduplicates in-flight requests.
 */
function ensureLoaded(tagName: string): void {
  if (loaded.has(tagName) || loading.has(tagName)) return;

  const fileName = COMPONENT_MAP[tagName];
  if (!fileName) return;

  const promise = loadComponent(tagName, fileName);
  loading.set(tagName, promise);

  promise
    .then(() => {
      loaded.add(tagName);
      // Elements may have been inserted while this module was being fetched.
      // Re-scan so anything the observer queued in that window is handled too.
      scanDocument();
    })
    .catch((err) => {
      console.error(
        `[regent-ui] Failed to load <${tagName}> from ${componentURL(fileName)} ` +
          `after ${MAX_LOAD_ATTEMPTS} attempts — the element will stay un-upgraded ` +
          `(no tabs, no data requests) until the page is reloaded.`,
        err,
      );
    })
    .finally(() => {
      loading.delete(tagName);
    });
}

/**
 * Dynamically import a component module by URL.
 * The import URL is fully runtime-computed so bundlers leave it alone.
 */
async function loadComponent(tagName: string, fileName: string): Promise<void> {
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= MAX_LOAD_ATTEMPTS; attempt++) {
    try {
      // Use dynamic import — this is a native ES module import at runtime.
      // The component module is expected to call customElements.define() as a side effect.
      await import(
        /* @vite-ignore */ /* webpackIgnore: true */ componentURLForAttempt(fileName, attempt)
      );

      // Wait for the custom element to be fully defined (safety check).
      await customElements.whenDefined(tagName);

      if (import.meta.env.DEV) {
        console.log(`[regent-ui] ✓ Loaded <${tagName}>`);
      }
      return;
    } catch (err) {
      lastError = err;
      if (attempt < MAX_LOAD_ATTEMPTS) {
        await delay(RETRY_DELAY_MS * attempt);
      }
    }
  }

  throw lastError;
}

// ── Initialisation ─────────────────────────────────────────────────────────

/**
 * Scan the current document for any existing regent-* elements.
 */
function scanDocument(): void {
  scanElement(document.documentElement);
}

/**
 * Set up a MutationObserver to detect newly added regent-* elements.
 *
 * This is the primary mechanism for catching <regent-*> elements that a
 * framework (e.g. Svelte) mounts after the loader starts — the observer fires
 * for every childList/subtree change, so elements added at any later point are
 * found and their modules fetched.
 */
function observeDOM(): void {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) {
          scanElement(node);
        }
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

/**
 * Expose a deterministic "scan now" hook so the host app can force a re-check
 * immediately after it has mounted content containing <regent-*> elements
 * (e.g. after an SPA route/tab swap or a large conditional render).
 *
 * This is a clean, opt-in alternative to polling: apps that know they just
 * changed the DOM can call `window.__regentUiScan()` (or dispatch
 * `document.dispatchEvent(new CustomEvent("regent-ui:scan"))`) to pick up any
 * element the observer may not have delivered yet. ensureLoaded() dedupes, so
 * calling it frequently is harmless.
 */
function exposeScanHandle(): void {
  const w = window as unknown as { __regentUiScan?: () => void };

  const scan = () => scanDocument();
  if (typeof w.__regentUiScan !== "function") {
    w.__regentUiScan = scan;
  }
  document.addEventListener("regent-ui:scan", scan);
}

// ── Start ──────────────────────────────────────────────────────────────────

// Run as soon as possible. If the script is in <head>, the body may not exist
// yet, so wait for the document to be ready before the first scan. The
// MutationObserver is registered in both branches, so elements that appear
// later (framework content, SPA navigation) are handled.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    scanDocument();
    observeDOM();
    exposeScanHandle();
  });
} else {
  scanDocument();
  observeDOM();
  exposeScanHandle();
}
